import React, { memo } from 'react';
import { motion } from 'framer-motion';
import INFINITE_SCROLL_CONFIG from '../../../config/infiniteScrollConfig';

const VentaCard = memo(({ venta, index, onVerDetalles, onImprimirTicket, onDevolucion }) => {
  // Nota: el backend guarda `venta.total` ya con el descuento aplicado (total final).
  // Aquí tratamos `venta.total` como el total final. Si hay descuento, el precio antes
  // del descuento será totalAntes = total + descuento.
  const total = parseFloat(venta.total) || 0;
  const descuento = parseFloat(venta.descuento) || 0;
  const totalAntes = total + descuento;
  
  return (
    <motion.div
      key={venta.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        delay: Math.min(
          index * INFINITE_SCROLL_CONFIG.ANIMATION_DELAY_INCREMENT, 
          INFINITE_SCROLL_CONFIG.MAX_ANIMATION_DELAY
        ) 
      }}
      className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-xl hover:border-purple-200 transition-all duration-200 transform hover:scale-[1.02]"
    >
      {/* Header de la venta */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-3 rounded-full">
            <span className="text-white text-lg">🧾</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Venta #{venta.id}</h3>
            <p className="text-sm text-gray-600">
              {new Date(venta.createdAt).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>
        {descuento > 0 && (
          <div className="bg-green-100 text-green-700 px-2 py-1 rounded-lg text-xs font-bold">
            💰 Descuento
          </div>
        )}
      </div>

      {/* Información del cliente y vendedor */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 gap-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">👤</span>
            <span className="text-gray-700 font-medium">Cliente:</span>
            <span className="text-gray-800">
              {venta.cliente?.nombre || 'Consumidor final'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">👨‍💼</span>
            <span className="text-gray-700 font-medium">Vendedor:</span>
            <span className="text-gray-800">
              {venta.usuario?.nombre_user || 'Sin vendedor'}
            </span>
          </div>
        </div>
      </div>

      {/* Información financiera */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-blue-700 font-medium">💰 Total:</span>
              <span className="text-blue-800 font-bold">${total.toFixed(2)}</span>
            </div>
            {descuento > 0 && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-green-700 font-medium">🎁 Descuento:</span>
                  <span className="text-green-700 font-bold">-${descuento.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Antes:</span>
                  <span className="text-gray-700 line-through">${totalAntes.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
      </div>

      {/* Botones de acción */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => onVerDetalles(venta)}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <span className="text-lg">👁️</span>
          Ver Detalles
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => onImprimirTicket(venta)}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <span className="text-lg">🖨️</span>
            <span className="hidden sm:inline">Imprimir</span>
          </button>
          <button
            onClick={() => onDevolucion(venta)}
            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <span className="text-lg">↩️</span>
            <span className="hidden sm:inline">Devolver</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
});

VentaCard.displayName = 'VentaCard';

export default VentaCard;
